import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { timeAgo } from "@/lib/format";
import { RiskBadge } from "@/components/StatusBadge";
import {
  BarChart3, Lock, Package, TriangleAlert, FileText, MessageSquare, CheckCircle2,
  Archive, Clock, ChevronRight, ShieldAlert, Layers, Timer,
} from "lucide-react";

const fmtDays = (d) => (d == null ? "—" : `${d}d`);

function SectionCard({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="bg-surface border border-line rounded-md overflow-hidden">
      <div className="px-5 py-3.5 border-b border-line flex items-center gap-2.5">
        <div>
          <h3 className="font-heading text-[15px] font-semibold text-ink leading-none">{title}</h3>
          {subtitle && <p className="text-[11.5px] text-ink-3 mt-1">{subtitle}</p>}
        </div>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function MetricCard({ label, value, tone, onClick }) {
  const color = value > 0 ? tone : "#A6AEB6";
  return (
    <button
      data-testid={`report-metric-${label.toLowerCase().replace(/\s+/g, "-")}`}
      onClick={onClick}
      disabled={!onClick}
      className={`text-left bg-surface border border-line-strong rounded-sm px-4 py-3.5 shadow-industrialCard transition-shadow ${onClick ? "hover:shadow-industrialHover cursor-pointer group" : "cursor-default"}`}
    >
      <div className="flex items-start justify-between">
        <div className="font-mono-data text-[24px] font-semibold leading-none" style={{ color }}>{value}</div>
        {onClick && <ChevronRight className="h-4 w-4 text-ink-3 opacity-0 group-hover:opacity-100 transition-opacity" />}
      </div>
      <div className="op-label mt-2 leading-tight">{label}</div>
    </button>
  );
}

function Bar({ label, value, max, color, sub, onClick }) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  const Wrap = onClick ? "button" : "div";
  return (
    <Wrap onClick={onClick} className={`block w-full text-left ${onClick ? "group cursor-pointer" : ""}`}>
      <div className="flex items-center justify-between mb-1">
        <span className={`text-[12.5px] ${onClick ? "text-ink group-hover:text-brand transition-colors" : "text-ink"}`}>{label}</span>
        <span className="font-mono-data text-[12px] text-ink-2">{sub != null ? sub : value}</span>
      </div>
      <div className="h-2 rounded-full bg-surface-alt overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: value > 0 ? color : "#D7DCE1" }} />
      </div>
    </Wrap>
  );
}

function StatRow({ label, value, tone, onClick }) {
  const color = value > 0 ? tone : "#7B8794";
  const Wrap = onClick ? "button" : "div";
  return (
    <Wrap
      data-testid={`report-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      onClick={onClick}
      className={`flex items-center justify-between py-2.5 border-b border-line last:border-0 w-full text-left ${onClick ? "group cursor-pointer" : ""}`}
    >
      <span className={`text-[13px] ${onClick ? "text-ink-2 group-hover:text-brand transition-colors" : "text-ink-2"}`}>{label}</span>
      <span className="flex items-center gap-2">
        <span className="font-mono-data text-[14px] font-semibold" style={{ color }}>{value}</span>
        {onClick && <ChevronRight className="h-3.5 w-3.5 text-ink-3 opacity-0 group-hover:opacity-100 transition-opacity" />}
      </span>
    </Wrap>
  );
}

export default function Reports() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (user && user.role !== "operator") client.get("/reports").then((res) => setR(res.data)).catch(() => setErr(true));
  }, [user]);

  if (user && user.role === "operator") {
    return (
      <Layout>
        <div data-testid="reports-restricted" className="bg-surface border border-line rounded-md p-12 text-center">
          <Lock className="h-6 w-6 text-ink-3 mx-auto mb-3" />
          <h2 className="font-heading text-lg font-medium text-ink">Reports are restricted</h2>
          <p className="text-sm text-ink-2 mt-1">Only Admin and Manager roles can access Reports.</p>
        </div>
      </Layout>
    );
  }

  if (err) return <Layout><div data-testid="reports-error" className="bg-surface border border-line rounded-md p-12 text-center text-sm text-ink-2">Couldn't load reports. Please refresh to try again.</div></Layout>;
  if (!r) return <Layout><div className="text-sm text-ink-3 font-mono-data py-20 text-center">Loading reports…</div></Layout>;

  const o = r.overview;
  const cuh = r.customer_update_health;
  const cp = r.closeout_performance;
  const na = r.needs_attention_summary;
  const go = (path) => () => navigate(path);

  const stageMax = Math.max(...r.by_stage.map((s) => s.count), 1);
  const tisMax = Math.max(...r.time_in_stage.filter((s) => s.stage !== "Closed").map((s) => s.avg_days), 1);
  const blockerMax = Math.max(...(cp.common_blockers.map((b) => b.count) || [1]), 1);

  return (
    <Layout>
      <div className="mb-5">
        <p className="op-label mb-1">Operational visibility</p>
        <h1 className="font-heading text-2xl font-semibold text-ink">Reports</h1>
        <p className="text-sm text-ink-2 mt-1">Shipment performance and daily operational pressure at a glance.</p>
      </div>

      {/* 1. Overview metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2.5 mb-6">
        <MetricCard label="Active Jobs" value={o.active_jobs} tone="#0B132B" onClick={go("/app/board?filter=active")} />
        <MetricCard label="Delayed Jobs" value={o.delayed_jobs} tone="#DC2626" onClick={go("/app/needs-attention?view=delayed")} />
        <MetricCard label="Missing Documents" value={o.missing_documents} tone="#D97706" onClick={go("/app/documents")} />
        <MetricCard label="Customer Updates Overdue" value={o.customer_updates_overdue} tone="#D97706" onClick={go("/app/customer-updates?filter=overdue")} />
        <MetricCard label="Jobs Ready to Close" value={o.jobs_ready_to_close} tone="#059669" onClick={go("/app/board?filter=ready-to-close")} />
        <MetricCard label="Closed Jobs" value={o.closed_jobs} tone="#64748B" onClick={go("/app/board?filter=closed")} />
        <MetricCard label="No Update in 24h" value={o.no_update_24h} tone="#D97706" onClick={go("/app/needs-attention?view=no-update")} />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* 2. Active jobs by stage */}
        <SectionCard icon={Layers} title="Active Jobs by Stage" subtitle="Where work sits across the lifecycle">
          <div className="space-y-3">
            {r.by_stage.map((s) => (
              <Bar key={s.stage} label={s.stage} value={s.count} max={stageMax}
                color={s.stage === "Closed" ? "#94A0AC" : s.stage === "Ready to Close" ? "#3F566E" : "#3F566E"}
                onClick={s.stage === "Closed" ? go("/app/board?filter=closed") : s.stage === "Ready to Close" ? go("/app/board?filter=ready-to-close") : undefined} />
            ))}
          </div>
        </SectionCard>

        {/* 3. Needs Attention summary */}
        <SectionCard icon={TriangleAlert} title="Needs Attention Summary" subtitle="Open operational risks right now">
          <div>
            <StatRow label="Delayed jobs" value={na.delayed} tone="#3F566E" onClick={go("/app/needs-attention?view=delayed")} />
            <StatRow label="Overdue next actions" value={na.overdue_next_actions} tone="#3F566E" onClick={go("/app/needs-attention?view=next-action")} />
            <StatRow label="Missing documents" value={na.missing_documents} tone="#3F566E" onClick={go("/app/documents")} />
            <StatRow label="POD missing" value={na.pod_missing} tone="#3F566E" onClick={go("/app/needs-attention?view=pod-missing")} />
            <StatRow label="Customer updates overdue" value={na.customer_updates_overdue} tone="#3F566E" onClick={go("/app/customer-updates?filter=overdue")} />
            <StatRow label="Closeout blocked" value={na.closeout_blocked} tone="#3F566E" onClick={go("/app/board?filter=closeout-blocked")} />
          </div>
        </SectionCard>

        {/* 4. Customer update health */}
        <SectionCard icon={MessageSquare} title="Customer Update Health" subtitle="Who's informed and who's waiting">
          <div className="grid grid-cols-3 gap-2.5 mb-4">
            {[["Due Today", cuh.updates_due_today, "#3F566E"], ["Overdue", cuh.updates_overdue, "#3F566E"], ["Updated 24h", cuh.recently_updated, "#3F566E"]].map(([l, v, c]) => (
              <div key={l} className="bg-surface-alt border border-line rounded-sm px-3 py-2.5">
                <div className="font-mono-data text-[18px] font-semibold leading-none" style={{ color: v > 0 ? c : "#A6AEB6" }}>{v}</div>
                <div className="op-label mt-1.5">{l}</div>
              </div>
            ))}
          </div>
          <p className="op-label mb-2">Customers with repeated overdue updates</p>
          {cuh.repeated_overdue.length === 0 ? (
            <p className="text-[12.5px] text-ink-3">None — no customer has multiple overdue updates.</p>
          ) : (
            <div className="space-y-1.5">
              {cuh.repeated_overdue.map((c) => (
                <div key={c.customer} data-testid={`repeat-overdue-${c.customer}`} className="flex items-center justify-between text-[13px]">
                  <span className="text-ink">{c.customer}</span>
                  <span className="font-mono-data text-danger font-medium">{c.count} overdue</span>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* 5. Closeout performance */}
        <SectionCard icon={CheckCircle2} title="Closeout Performance" subtitle="Throughput and friction at the finish line">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-4">
            {[
              ["Ready to Close", cp.ready_to_close, "#3F566E"],
              ["Blocked", cp.closeout_blocked, "#3F566E"],
              ["Closed / 7d", cp.closed_this_week, "#516171"],
              ["Avg D→Closed", fmtDays(cp.avg_delivered_to_closed_days), "#27384A"],
            ].map(([l, v, c]) => (
              <div key={l} className="bg-surface-alt border border-line rounded-sm px-3 py-2.5">
                <div className="font-mono-data text-[18px] font-semibold leading-none" style={{ color: c }}>{v}</div>
                <div className="op-label mt-1.5">{l}</div>
              </div>
            ))}
          </div>
          <p className="op-label mb-2.5">Common closeout blockers</p>
          {cp.common_blockers.length === 0 ? (
            <p className="text-[12.5px] text-ink-3">No active closeout blockers.</p>
          ) : (
            <div className="space-y-2.5">
              {cp.common_blockers.map((b) => (
                <Bar key={b.label} label={b.label} value={b.count} max={blockerMax} color="#3F566E" />
              ))}
            </div>
          )}
        </SectionCard>
      </div>

      {/* 7. Time in stage */}
      <div className="mt-5">
        <SectionCard icon={Timer} title="Average Time in Stage" subtitle="How long jobs currently sit in each stage">
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {r.time_in_stage.filter((s) => s.stage !== "Closed").map((s) => (
              <Bar key={s.stage} label={`${s.stage}`} value={s.avg_days} max={tisMax} color="#3F566E"
                sub={`${fmtDays(s.avg_days)} · ${s.count} job${s.count === 1 ? "" : "s"}`} />
            ))}
          </div>
        </SectionCard>
      </div>

      {/* 6. No update in 24h */}
      <div className="mt-5">
        <SectionCard icon={Clock} title="No Update in 24h" subtitle={`${r.no_update_jobs.length} active job${r.no_update_jobs.length === 1 ? "" : "s"} untouched for over a day`}>
          {r.no_update_jobs.length === 0 ? (
            <p className="text-[13px] text-ink-3">Every active job has been updated in the last 24 hours.</p>
          ) : (
            <div className="overflow-x-auto mv-scroll -mx-5 -my-5">
              <table className="w-full text-left">
                <thead><tr className="border-b border-line bg-surface-alt">
                  {["Job #", "Customer", "Stage", "Owner", "Risk", "Last Update", ""].map((h) => (
                    <th key={h} className="op-label px-5 py-2.5 whitespace-nowrap font-semibold">{h}</th>
                  ))}
                </tr></thead>
                <tbody className="divide-y divide-line">
                  {r.no_update_jobs.map((j) => (
                    <tr key={j.id} data-testid={`no-update-row-${j.job_number}`} onClick={() => navigate(`/app/shipments/${j.id}`)}
                      className="hover:bg-surface-alt transition-colors cursor-pointer group">
                      <td className="px-5 py-3 font-mono-data text-[12.5px] text-ink font-medium whitespace-nowrap">{j.job_number}</td>
                      <td className="px-5 py-3 text-[13px] text-ink whitespace-nowrap">{j.customer_name}</td>
                      <td className="px-5 py-3 text-[13px] text-ink-2 whitespace-nowrap">{j.current_stage}</td>
                      <td className="px-5 py-3 text-[13px] text-ink-2 whitespace-nowrap">{j.assigned_owner}</td>
                      <td className="px-5 py-3 whitespace-nowrap"><RiskBadge status={j.risk_status} /></td>
                      <td className="px-5 py-3 text-[12.5px] text-danger font-medium whitespace-nowrap">{timeAgo(j.last_updated_at)}</td>
                      <td className="px-5 py-3"><ChevronRight className="h-4 w-4 text-ink-3 opacity-0 group-hover:opacity-100 transition-opacity" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      </div>
    </Layout>
  );
}
